import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useModalLayer } from '../../../lib/modalStack';
import { confirmDialog } from '../../../lib/confirmStore';
import {
  Award,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  FileText,
  Info,
  Loader2,
  Mail,
  Megaphone,
  Plus,
  ScrollText,
  Workflow,
  X,
  Paperclip,
  DollarSign,
  Building2,
} from 'lucide-react';
import ProgramPricingVariablesEditor from './ProgramPricingVariablesEditor';
import ProgramAcdEditor from './ProgramAcdEditor';
import { safeFetch } from '../../../lib/api';
import { getActiveWorkspaceId } from '../../../lib/workspaces';
import {
  normaliseProgram,
  resolveTermsAndConditions,
  serialiseProgram,
  type ProgramConfig,
  type ProgramDefaults,
} from '../../../lib/programConfig';
import { RichTextContent, RichTextEditor, isRichTextEmpty } from '../../shared/RichTextEditor';
import SearchableSelect from '../../shared/SearchableSelect';
import RequiredDocumentsEditor from './RequiredDocumentsEditor';
import ProgramSkillsEditor from './ProgramSkillsEditor';
import PipelineEditor from './PipelineEditor';
import ProgramResourcesEditor from './ProgramResourcesEditor';
import { ProgramPhasesEditor } from './ProgramPhasesEditor';

/**
 * Program configuration workspace.
 *
 * Every program-specific value the platform reads is edited here, which is what
 * makes the program the single source of truth: content, required documents,
 * the program's own master pipeline, legal terms, and training/marketing
 * resources.
 */

type TabId = 'content' | 'pricingVariables' | 'acdPrice' | 'welcomeEmail' | 'phases' | 'documents' | 'skills' | 'pipeline' | 'terms' | 'termSeason' | 'training' | 'marketing';

const TABS: Array<{ id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'content', label: 'Content', icon: Info },
  { id: 'pricingVariables', label: 'Pricing Variables', icon: DollarSign },
  { id: 'acdPrice', label: 'Price & ACD', icon: Building2 },
  { id: 'welcomeEmail', label: 'Welcome Email', icon: Mail },
  { id: 'phases', label: 'Phases', icon: Workflow },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'skills', label: 'Skills & Abilities', icon: Award },
  { id: 'pipeline', label: 'Pipeline', icon: Workflow },
  { id: 'terms', label: 'Terms', icon: ScrollText },
  { id: 'termSeason', label: 'Term/Season', icon: CalendarDays },
  { id: 'training', label: 'Training', icon: BookOpen },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
];

interface Props {
  /** Raw program record (any legacy shape) — normalised on open. */
  program: unknown;
  allPrograms: ProgramConfig[];
  /** Workspace / global fallbacks used by the Terms preview. */
  defaults?: ProgramDefaults;
  onClose: () => void;
  onSaved: (program: ProgramConfig) => void;
  onToast?: (message: string, type?: string) => void;
}

export default function ProgramConfigModal({
  program: rawProgram,
  allPrograms,
  defaults,
  onClose,
  onSaved,
  onToast,
}: Props) {
  const initial = useMemo(() => normaliseProgram(rawProgram), [rawProgram]);
  const [draft, setDraft] = useState<ProgramConfig | null>(initial);
  const [tab, setTab] = useState<TabId>('content');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ADM-12: every close path (Escape via the modal stack, backdrop click,
  // the header X button) funnels through this guarded close, so unsaved
  // edits always get the discard confirmation (confirmDialog, not the
  // native window.confirm this handler used before).
  const handleClose = useCallback(async () => {
    if (dirty) {
      const discard = await confirmDialog('Discard unsaved changes to this program?', {
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
      });
      if (!discard) return;
    }
    onClose();
  }, [dirty, onClose]);

  const zIndex = useModalLayer(!!draft, handleClose);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ADM-12: focus management and body scroll lock mirroring the shared
  // Modal — focus enters the panel on open, Tab stays trapped inside it,
  // and focus returns to the opener on close. Runs on the closed-to-open
  // transition only; the draft object changes on every edit and must not
  // retrigger it.
  const isOpen = !!draft;
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const focusableSelector =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"]';

    const prevFocus = document.activeElement as HTMLElement | null;
    const savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const first = panel.querySelector<HTMLElement>(focusableSelector);
        if (first) {
          first.focus();
        } else {
          panel.setAttribute('tabindex', '-1');
          panel.focus();
        }
      });
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const nodes = panel.querySelectorAll<HTMLElement>(focusableSelector);
      if (nodes.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === panel) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = savedOverflow;
      // Return focus to the opener, unless focus has already moved into a
      // dialog that is still open above this one.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === document.body || panel.contains(active)) {
        prevFocus?.focus();
      }
    };
  }, [isOpen]);

  const [sponsorsList, setSponsorsList] = useState<any[]>([]);
  const [providersList, setProvidersList] = useState<any[]>([]);
  const [emailTemplatesList, setEmailTemplatesList] = useState<Array<{ id: string; name: string; code?: string; subject?: string; htmlBody?: string }>>([]);
  const [invoiceTemplatesList, setInvoiceTemplatesList] = useState<any[]>([]);

  useEffect(() => {
    safeFetch('/api/admin/sponsors')
      .then((data) => {
        if (data?.sponsors) setSponsorsList(data.sponsors);
      })
      .catch((err) => console.warn('Failed to load sponsors:', err));

    safeFetch('/api/admin/service-providers')
      .then((data) => {
        if (data?.serviceProviders || data?.providers) {
          setProvidersList(data.serviceProviders || data.providers);
        }
      })
      .catch((err) => console.warn('Failed to load service providers:', err));

    safeFetch('/api/email-templates')
      .then((data) => {
        if (Array.isArray(data?.templates)) setEmailTemplatesList(data.templates);
      })
      .catch((err) => console.warn('Failed to load email templates:', err));

    safeFetch('/api/admin/finance/invoice-templates')
      .then((data) => {
        if (Array.isArray(data?.templates)) setInvoiceTemplatesList(data.templates);
      })
      .catch((err) => console.warn('Failed to load invoice templates:', err));
  }, []);

  const patch = useCallback((changes: Partial<ProgramConfig>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
    setDirty(true);
  }, []);

  /** Uploads through the existing admin endpoint used by the old drawer. */
  const uploadMaterial = useCallback(
    async (file: File, materialType: 'training' | 'marketing') => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const data = await safeFetch('/api/admin/programs/upload-material', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileBase64: base64,
          contentType: file.type,
          programId: draft?.id,
          materialType,
        }),
      });

      if (!data?.downloadUrl) throw new Error(data?.error || 'Upload failed');
      return { url: data.downloadUrl as string, mimeType: file.type, sizeBytes: file.size };
    },
    [draft?.id]
  );

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      setError('Program name is required.');
      setTab('content');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const workspaceId = getActiveWorkspaceId();
      const payload = serialiseProgram({
        ...draft,
        // Never let a program escape its workspace.
        workspaceId: draft.workspaceId || workspaceId || undefined,
      });

      await safeFetch(`/api/admin/programs/${encodeURIComponent(draft.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      setDirty(false);
      onToast?.('Program configuration saved', 'success');
      onSaved(draft);
    } catch (err: any) {
      const message = err?.message || 'Could not save this program.';
      setError(message);
      onToast?.(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!draft) {
    return null;
  }

  const terms = resolveTermsAndConditions(draft, defaults);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-stretch justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={`Configure ${draft.name}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) void handleClose();
      }}
    >
      <div ref={panelRef} className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-[min(90vh,900px)] sm:max-w-5xl sm:rounded-2xl">
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-slate-900">
              {draft.name || 'Untitled program'}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              This configuration is the source of truth for every portal, form, email and report
              that references this program.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {dirty && (
              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                Unsaved
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl btn-primary px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Tabs */}
        <nav
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 px-3 py-2"
          role="tablist"
          aria-label="Program configuration sections"
        >
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                tab === id
                  ? 'bg-brand-active text-brand'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </nav>

        {error && (
          <p className="shrink-0 border-b border-red-200 bg-red-50 px-5 py-2 text-xs font-semibold text-red-700">
            {error}
          </p>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {tab === 'content' && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Program name *</span>
                  <input
                    value={draft.name}
                    onChange={(event) => patch({ name: event.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Short code</span>
                  <input
                    value={draft.code || ''}
                    onChange={(event) => patch({ code: event.target.value.toUpperCase() })}
                    maxLength={20}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Minimum applicant age</span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={draft.minimumAge ?? ''}
                    placeholder="18 (default)"
                    onChange={(event) => patch({ minimumAge: event.target.value === '' ? undefined : Number(event.target.value) })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                  />
                  <span className="block text-[11px] text-slate-500">Why: applicants must meet the program minimum age (default 18) — enforced when the application is submitted.</span>
                </label>
              </div>

              <label className="block space-y-1">
                <span className="text-xs font-semibold text-slate-600">
                  Short description (cards and dropdowns)
                </span>
                <textarea
                  value={draft.description}
                  rows={2}
                  onChange={(event) => patch({ description: event.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
                />
              </label>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-600">Program overview</span>
                <RichTextEditor
                  value={draft.overview}
                  onChange={(html) => patch({ overview: html })}
                  placeholder="Full description shown on the program page…"
                  ariaLabel="Program overview"
                />
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-600">Eligibility requirements</span>
                <RichTextEditor
                  value={draft.eligibility}
                  onChange={(html) => patch({ eligibility: html })}
                  placeholder="Who can apply to this program…"
                  ariaLabel="Eligibility requirements"
                />
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-600">Applicant instructions</span>
                <RichTextEditor
                  value={draft.instructions}
                  onChange={(html) => patch({ instructions: html })}
                  placeholder="Guidance shown to applicants during the application…"
                  ariaLabel="Applicant instructions"
                />
              </div>

              <div className="space-y-1">
                <span className="text-xs font-semibold text-slate-600">Marketing content</span>
                <RichTextEditor
                  value={draft.marketingContent}
                  onChange={(html) => patch({ marketingContent: html })}
                  placeholder="Copy partners and referrers may use to promote this program…"
                  ariaLabel="Marketing content"
                />
              </div>

              <div className="space-y-1">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  Processing notes
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                    Internal only
                  </span>
                </span>
                <RichTextEditor
                  value={draft.processingNotes}
                  onChange={(html) => patch({ processingNotes: html })}
                  placeholder="Never shown to applicants — internal handling notes…"
                  ariaLabel="Processing notes"
                  compact
                />
              </div>

              <div className="space-y-1">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                  Internal notes — AI guidance
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-600">
                    Internal only · Guides AI
                  </span>
                </span>
                <RichTextEditor
                  value={draft.internalNotes}
                  onChange={(html) => patch({ internalNotes: html })}
                  placeholder="Guidance the AI follows when evaluating eligibility and screening this program's applicants (e.g. accept final-year students; nursing diplomas need a transcript check)…"
                  ariaLabel="Internal notes for AI guidance"
                  compact
                />
                <p className="text-[11px] text-slate-500">
                  Never shown to applicants. The AI eligibility check reads these notes as admin guidance for this program.
                </p>
              </div>

              <label className="flex items-center gap-2 border-t border-slate-100 pt-4">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(event) => patch({ active: event.target.checked })}
                  className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                />
                <span className="text-xs font-semibold text-slate-700">
                  Program is active and accepting applications
                </span>
              </label>

              <div className="border-t border-slate-100 pt-4 space-y-4">
                <div>
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Access & Partnership</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Restrict program visibility to specific Sponsors and Service Providers.</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600 block">Allowed Sponsors (Multi-Select)</span>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-2">
                      {sponsorsList.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">No sponsors found.</p>
                      ) : (
                        sponsorsList.map((sponsor) => {
                          const sponsorId = sponsor.sponsorId || sponsor.id;
                          const checked = (draft.allowedSponsors || []).includes(sponsorId);
                          return (
                            <label key={sponsorId} className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const updated = checked
                                    ? (draft.allowedSponsors || []).filter((id) => id !== sponsorId)
                                    : [...(draft.allowedSponsors || []), sponsorId];
                                  patch({ allowedSponsors: updated });
                                }}
                                className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                              />
                              <span>{sponsor.name}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600 block">Allowed Service Providers</span>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-2">
                      {providersList.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">No service providers found.</p>
                      ) : (
                        providersList.map((provider) => {
                          const providerId = provider.id || provider.providerId;
                          const checked = (draft.allowedServiceProviders || []).includes(providerId);
                          return (
                            <label key={providerId} className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const updated = checked
                                    ? (draft.allowedServiceProviders || []).filter((id) => id !== providerId)
                                    : [...(draft.allowedServiceProviders || []), providerId];
                                  patch({ allowedServiceProviders: updated });
                                }}
                                className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                              />
                              <span>{provider.name}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'pricingVariables' && (
            <ProgramPricingVariablesEditor
              config={draft}
              onChange={(updated) => { setDraft(updated); setDirty(true); }}
            />
          )}

          {tab === 'acdPrice' && (
            <ProgramAcdEditor
              config={draft}
              sponsorsList={sponsorsList}
              invoiceTemplatesList={invoiceTemplatesList}
              onChange={(updated) => { setDraft(updated); setDirty(true); }}
            />
          )}

          {tab === 'welcomeEmail' && (() => {
            const welcome = draft.welcomeEmail || { enabled: false, mode: 'template' };

            const handleAttachmentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) { onToast?.('Attachment file size must be under 5MB', 'error'); return; }
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = (reader.result as string).split(',')[1];
                const currentAtts = welcome.attachments || [];
                patch({
                  welcomeEmail: {
                    ...welcome,
                    attachments: [...currentAtts, { name: file.name, base64Content: base64, mimeType: file.type || 'application/pdf' }],
                  },
                });
              };
              reader.readAsDataURL(file);
              e.target.value = '';
            };

            const selectedTemplate = emailTemplatesList.find(t => t.id === welcome.templateId);

            return (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-150 rounded-2xl flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider">Automated Welcome Email Trigger</h4>
                    <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">
                      Configure the automated Welcome Email sent immediately to applicants when their application is submitted. You can select an existing Email Template or manually enter custom HTML content and attach PDF files.
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={welcome.enabled || false}
                      onChange={(e) => patch({ welcomeEmail: { ...welcome, enabled: e.target.checked } })}
                      className="h-4 w-4 text-indigo-600 rounded border-slate-300 focus:ring-brand-primary cursor-pointer"
                    />
                    <span className="text-xs font-bold text-slate-900">
                      Enable Automated Welcome Email on Application Submission
                    </span>
                  </label>

                  {welcome.enabled && (
                    <div className="pt-2 space-y-4 border-t border-slate-100">
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                          <input
                            type="radio"
                            name="welcomeMode"
                            checked={welcome.mode !== 'manual'}
                            onChange={() => patch({ welcomeEmail: { ...welcome, mode: 'template' } })}
                            className="text-indigo-600 focus:ring-brand-primary cursor-pointer"
                          />
                          Select Email Template
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                          <input
                            type="radio"
                            name="welcomeMode"
                            checked={welcome.mode === 'manual'}
                            onChange={() => patch({ welcomeEmail: { ...welcome, mode: 'manual' } })}
                            className="text-indigo-600 focus:ring-brand-primary cursor-pointer"
                          />
                          Manually Edit Email & Attach File
                        </label>
                      </div>

                      {welcome.mode !== 'manual' ? (
                        <div className="space-y-3">
                          <label className="text-xs font-semibold text-slate-600 block">Select Email Template *</label>
                          <SearchableSelect
                            value={welcome.templateId || ''}
                            onChange={(v) => patch({ welcomeEmail: { ...welcome, templateId: v } })}
                            options={emailTemplatesList.map(t => ({
                              value: t.id,
                              label: `${t.code ? `[${t.code}] ` : ''}${t.name}`,
                            }))}
                            placeholder="Search email templates..."
                            clearLabel="Select a template..."
                            emptyLabel="No email templates found"
                          />
                          {selectedTemplate && (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1 text-slate-700">
                              <p><span className="font-bold text-slate-900">Subject:</span> {selectedTemplate.subject}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-600 block">Email Subject Line *</label>
                            <input
                              type="text"
                              value={welcome.subject || ''}
                              onChange={(e) => patch({ welcomeEmail: { ...welcome, subject: e.target.value } })}
                              placeholder="e.g. Welcome to {{program_name}} - Your Application is Received!"
                              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary/20"
                            />
                            <p className="text-[11px] text-slate-500">Supported merge tags: {"{{first_name}}, {{last_name}}, {{program_name}}, {{application_id}}"}</p>
                          </div>

                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-600 block">Email Content (HTML / Text) *</label>
                            <textarea
                              rows={6}
                              value={welcome.htmlBody || ''}
                              onChange={(e) => patch({ welcomeEmail: { ...welcome, htmlBody: e.target.value } })}
                              placeholder="Enter message body or HTML template..."
                              className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-primary/20"
                            />
                          </div>

                          <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-600 block">Attach PDF / Documents</label>
                            {(welcome.attachments || []).length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2">
                                {welcome.attachments?.map((att, idx) => (
                                  <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700">
                                    <Paperclip className="h-3 w-3 text-slate-400" />
                                    <span>{att.name}</span>
                                    <button aria-label="Remove attachment" title="Remove attachment"
                                      type="button"
                                      onClick={() => {
                                        const updated = (welcome.attachments || []).filter((_, i) => i !== idx);
                                        patch({ welcomeEmail: { ...welcome, attachments: updated } });
                                      }}
                                      className="text-slate-400 hover:text-rose-600 cursor-pointer ml-1"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer shadow-2xs">
                              <Paperclip className="h-3.5 w-3.5 text-slate-500" />
                              <span>Attach PDF / File</span>
                              <input type="file" onChange={handleAttachmentUpload} className="hidden" />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {tab === 'phases' && (
            <ProgramPhasesEditor
              programId={draft.id}
              allPrograms={allPrograms}
              onToast={onToast}
            />
          )}

          {tab === 'documents' && (
            <RequiredDocumentsEditor
              documents={draft.requiredDocuments}
              onChange={(requiredDocuments) => patch({ requiredDocuments })}
            />
          )}

          {tab === 'skills' && (
            <ProgramSkillsEditor
              config={draft.skillsConfig ?? { selectionMode: 'multiple', maxSelections: null, skills: [] }}
              onChange={(skillsConfig) => patch({ skillsConfig })}
            />
          )}

          {tab === 'pipeline' && (
            <PipelineEditor
              program={draft}
              allPrograms={allPrograms}
              onChange={(changes) => patch(changes)}
            />
          )}

          {tab === 'terms' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Terms &amp; conditions</h3>
                <p className="text-xs text-slate-500">
                  Shown to applicants before submission. Leave blank to fall back to the global
                  default — the portal never displays empty terms.
                </p>
              </div>

              <RichTextEditor
                value={draft.termsAndConditions}
                onChange={(html) => patch({ termsAndConditions: html })}
                placeholder="Program-specific terms and conditions…"
                minHeight={260}
                ariaLabel="Terms and conditions"
              />

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  What applicants will see
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] ${
                      terms.source === 'program'
                        ? 'bg-emerald-50 text-emerald-700'
                        : terms.source === 'default'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {terms.source === 'program'
                      ? 'Program-specific'
                      : terms.source === 'default'
                        ? 'Global default'
                        : 'Nothing configured'}
                  </span>
                </p>
                <RichTextContent
                  html={terms.content}
                  className="max-h-64 overflow-y-auto rounded-lg bg-white p-3 text-sm"
                  fallback={
                    <p className="rounded-lg bg-white p-3 text-xs text-red-600">
                      No program terms and no global default are configured. Applicants would see an
                      empty terms step — set one of the two.
                    </p>
                  }
                />
                {terms.source === 'default' && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    This program has no terms of its own, so the global default is displayed.
                  </p>
                )}
              </div>

              {isRichTextEmpty(draft.termsAndConditions) && !defaults?.termsAndConditions && (
                <p className="text-[11px] text-slate-500">
                  Tip: configure a global default in Workspace settings so every program has a
                  guaranteed fallback.
                </p>
              )}
            </div>
          )}

          {tab === 'termSeason' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Program Terms / Seasons</h3>
                  <p className="text-xs text-slate-500">
                    Create or edit the list of intakes/terms for this program. These are shown to applicants during application creation.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newTerm = {
                      termId: `term_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`,
                      label: '',
                      startDate: '',
                      endDate: '',
                      applicationDeadline: '',
                      isOpen: true,
                      capacity: 0,
                      notes: '',
                    };
                    patch({ intakes: [...(draft.intakes || []), newTerm] });
                  }}
                  className="inline-flex items-center gap-1 rounded-xl bg-blue-50 px-3 py-1.5 text-xs font-semibold text-brand hover:bg-blue-100 transition cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Term
                </button>
              </div>

              <div className="space-y-4">
                {(draft.intakes || []).map((term, idx) => (
                  <div key={term.termId || idx} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Term {idx + 1}
                      </span>
                      <button aria-label="Remove intake" title="Remove intake"
                        type="button"
                        onClick={() => {
                          const updated = (draft.intakes || []).filter((_, i) => i !== idx);
                          patch({ intakes: updated });
                        }}
                        className="p-1 text-red-400 hover:text-red-600 cursor-pointer hover:bg-red-50 rounded-lg transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1 sm:col-span-2">
                        <label className="text-[11px] font-semibold text-slate-600 block">Label *</label>
                        <input
                          value={term.label}
                          onChange={(e) => {
                            const updated = [...(draft.intakes || [])];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            patch({ intakes: updated });
                          }}
                          placeholder="Summer 2026"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-slate-600 block">Start Date *</label>
                        <input
                          type="date"
                          value={term.startDate || ''}
                          onChange={(e) => {
                            const updated = [...(draft.intakes || [])];
                            updated[idx] = { ...updated[idx], startDate: e.target.value };
                            patch({ intakes: updated });
                          }}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-slate-600 block">End Date *</label>
                        <input
                          type="date"
                          value={term.endDate || ''}
                          onChange={(e) => {
                            const updated = [...(draft.intakes || [])];
                            updated[idx] = { ...updated[idx], endDate: e.target.value };
                            patch({ intakes: updated });
                          }}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-slate-600 block">App Deadline *</label>
                        <input
                          type="date"
                          value={term.applicationDeadline || ''}
                          onChange={(e) => {
                            const updated = [...(draft.intakes || [])];
                            updated[idx] = { ...updated[idx], applicationDeadline: e.target.value };
                            patch({ intakes: updated });
                          }}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[11px] font-semibold text-slate-600 block">Capacity (0=∞)</label>
                        <input
                          type="number"
                          value={term.capacity || 0}
                          onChange={(e) => {
                            const updated = [...(draft.intakes || [])];
                            updated[idx] = { ...updated[idx], capacity: parseInt(e.target.value) || 0 };
                            patch({ intakes: updated });
                          }}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20 bg-white"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [...(draft.intakes || [])];
                          updated[idx] = { ...updated[idx], isOpen: !updated[idx].isOpen };
                          patch({ intakes: updated });
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                          term.isOpen ? 'bg-green-600' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            term.isOpen ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-[11px] font-semibold text-slate-600">Term Open</span>
                    </div>
                  </div>
                ))}

                {(!draft.intakes || draft.intakes.length === 0) && (
                  <p className="text-xs text-slate-500 text-center py-6 border border-dashed border-slate-200 rounded-xl bg-slate-50">
                    No terms or seasons defined. Click "+ Add Term" above to add one.
                  </p>
                )}
              </div>
            </div>
          )}

          {tab === 'training' && (
            <ProgramResourcesEditor
              collection="training"
              resources={draft.trainingResources}
              onChange={(trainingResources) => patch({ trainingResources })}
              onUpload={(file) => uploadMaterial(file, 'training')}
            />
          )}

          {tab === 'marketing' && (
            <ProgramResourcesEditor
              collection="marketing"
              resources={draft.marketingResources}
              onChange={(marketingResources) => patch({ marketingResources })}
              onUpload={(file) => uploadMaterial(file, 'marketing')}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
